import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCountry } from "../context/CountryContext";
import {
  GasPump, Truck, UsersThree, CurrencyCircleDollar, SquaresFour, ArrowsClockwise, SignOut, ChartBar, AddressBook, Stack, Scales, ShieldStar, Handshake, Receipt, RadioButton, Bell, X, MapPin,
} from "@phosphor-icons/react";

// nav items, with a `fuel` flag for entries that depend on the GH fuel station
const items = [
  { to: "/",              label: "Dashboard",       icon: SquaresFour,       end: true, testid: "nav-dashboard" },
  { to: "/control-tower", label: "Control Tower",   icon: RadioButton,                  testid: "nav-control-tower" },
  { to: "/map",           label: "Fleet Map",        icon: MapPin,                       testid: "nav-map" },
  { to: "/alerts",        label: "Alerts",           icon: Bell,                        testid: "nav-alerts", badge: "alerts" },
  { to: "/activities",    label: "Trips",            icon: Stack,                       testid: "nav-activities" },
  { to: "/contracts",     label: "Contracts",        icon: Handshake,                   testid: "nav-contracts" },
  // Evacuations & Waybills were folded into Contracts and Trips on 2026-06-25; routes still
  // exist for old bookmarks and direct deep-links, but they're no longer in the sidebar.
  { to: "/dispenses",     label: "Dispenses",        icon: GasPump,         fuel: true, testid: "nav-dispenses" },
  { to: "/deliveries",    label: "Deliveries",       icon: ArrowsClockwise, fuel: true, testid: "nav-deliveries" },
  { to: "/money",         label: "Money on Road",    icon: CurrencyCircleDollar,        testid: "nav-money" },
  { to: "/road-expenses", label: "Road Expenses",    icon: Scales,                      testid: "nav-road-expenses" },
  { to: "/invoicing",     label: "Invoicing",        icon: Receipt,                     testid: "nav-invoicing" },
  { to: "/customers",     label: "Customers",        icon: AddressBook,                 testid: "nav-customers" },
  { to: "/trucks",        label: "Trucks",           icon: Truck,                       testid: "nav-trucks" },
  { to: "/drivers",       label: "Drivers",          icon: UsersThree,                  testid: "nav-drivers" },
  { to: "/reports",       label: "Reports",          icon: ChartBar,                    testid: "nav-reports" },
  { to: "/users",         label: "Users",            icon: ShieldStar,   adminOnly: true, testid: "nav-users" },
];

const SCOPE_OPTIONS = [
  { key: "ALL", flag: "🌍", label: "All",   testid: "scope-all" },
  { key: "GH",  flag: "🇬🇭", label: "Ghana", testid: "scope-gh" },
  { key: "TG",  flag: "🇹🇬", label: "Togo",  testid: "scope-tg" },
  { key: "BJ",  flag: "🇧🇯", label: "Benin", testid: "scope-bj" },
];

export default function Sidebar({ mobileOpen = false, onCloseMobile = () => {} } = {}) {
  const { user, logout } = useAuth();
  const { scope, setScope, showFuel, allowedCountries, canSeeAll } = useCountry();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const visibleItems = items.filter((it) => {
    if (it.fuel && !showFuel) return false;
    if (it.adminOnly && user?.role !== "admin") return false;
    return true;
  });

  const [alertsOpen, setAlertsOpen] = useState(0);
  useEffect(() => {
    let alive = true;
    const fetchAlerts = async () => {
      try {
        const { data } = await api.get("/gps/alerts/summary");
        if (alive) setAlertsOpen(data?.open_count || 0);
      } catch { /* ignore */ }
    };
    fetchAlerts();
    const t = setInterval(fetchAlerts, 60000);     // refresh every 60 s
    return () => { alive = false; clearInterval(t); };
  }, []);

  const visibleScopes = SCOPE_OPTIONS.filter((o) => {
    if (o.key === "ALL") return canSeeAll;
    return allowedCountries.includes(o.key);
  });

  return (
    <>
      {/* Mobile backdrop — only rendered when drawer is open on small screens */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={onCloseMobile}
          data-testid="sidebar-mobile-backdrop"
          className="fixed inset-0 z-40 bg-slate-900/50 md:hidden"
        />
      )}
      <aside
        className={`w-64 shrink-0 border-r border-slate-200 bg-white flex flex-col
          fixed inset-y-0 left-0 z-50 transition-transform duration-200 ease-out
          md:static md:translate-x-0 md:z-auto md:min-h-screen
          ${mobileOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full"}`}
        data-testid="sidebar"
      >
      {/* Brand — with close button on mobile */}
      <div className="px-5 py-5 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="https://www.portside.nl/portside-logo-rgb.svg" alt="Portside" className="w-10 h-10" />
          <div className="leading-tight">
            <div className="font-display font-extrabold text-base tracking-tight text-ps-deep">PORTSIDE</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-ps-blue font-semibold">Yard Operations</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onCloseMobile}
          data-testid="sidebar-mobile-close"
          className="md:hidden p-1 rounded hover:bg-slate-100"
          aria-label="Close menu"
        >
          <X size={20} weight="bold" />
        </button>
      </div>

      {/* Country scope switcher */}
      <div className="px-3 py-3 border-b border-slate-200 bg-slate-50">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold px-2 mb-1.5">Country scope</div>
        <div className="flex gap-1 bg-white border border-slate-200 rounded-md p-1" data-testid="country-switcher">
          {visibleScopes.map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => setScope(o.key)}
              data-testid={o.testid}
              aria-pressed={scope === o.key}
              className={`flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 rounded text-xs font-semibold transition-colors ${
                scope === o.key
                  ? "bg-ps-blue text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <span className="text-sm leading-none">{o.flag}</span>
              <span className="md:hidden xl:inline">{o.label}</span>
            </button>
          ))}
        </div>
        {(scope === "TG" || scope === "BJ") && (
          <div className="mt-2 px-2 text-[10px] text-slate-500 leading-snug" data-testid={`scope-${scope.toLowerCase()}-hint`}>
            {scope === "TG" ? "Togo" : "Benin"}: no yard fuel station. Fuel modules are hidden. CMRs print in French.
          </div>
        )}
      </div>

      <nav className="py-3 flex-1">
        {visibleItems.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={it.testid}
            onClick={onCloseMobile}
            className={({ isActive }) =>
              `flex items-center gap-3 px-5 py-2.5 mx-2 my-0.5 text-sm rounded-md transition-colors duration-150 ${
                isActive
                  ? "bg-ps-blue text-white font-semibold shadow-sm"
                  : "text-slate-600 hover:bg-slate-100 hover:text-ps-deep"
              }`
            }
          >
            <it.icon size={18} weight="duotone" />
            <span className="flex-1">{it.label}</span>
            {it.badge === "alerts" && alertsOpen > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-ps-red text-white" data-testid="nav-alerts-badge">
                {alertsOpen > 99 ? "99+" : alertsOpen}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="px-5 py-4 border-t border-slate-200">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Signed in</div>
        <div className="text-sm font-semibold truncate text-ps-deep" data-testid="current-user-email">{user?.email}</div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="mt-3 w-full flex items-center justify-center gap-2 border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-ps-deep hover:text-white hover:border-ps-deep transition-colors duration-150"
        >
          <SignOut size={16} weight="bold" />
          Sign out
        </button>
      </div>
    </aside>
    </>
  );
}
